import { supabase } from '@/integrations/supabase/client';

export interface EmployeePayslipPreferences {
  id: string;
  employee_id: string;
  enable_password_protection: boolean;
  use_login_password: boolean;
  custom_password_hash?: string;
  auto_download_payslips: boolean;
  email_payslips: boolean;
  preferred_language: string;
  require_password_change: boolean;
  password_last_updated?: string;
  created_at: string;
  updated_at: string;
}

export interface EmployeePayslipPreferencesUpdate {
  enable_password_protection?: boolean;
  use_login_password?: boolean;
  custom_password?: string; // Plain text password to be hashed
  auto_download_payslips?: boolean;
  email_payslips?: boolean;
  preferred_language?: string;
}

/**
 * Get employee payslip preferences (creates defaults if none exist)
 */
export async function getEmployeePayslipPreferences(employeeId: string): Promise<EmployeePayslipPreferences> {
  const { data, error } = await supabase.rpc('get_employee_payslip_preferences', {
    p_employee_id: employeeId
  });

  if (error) {
    throw new Error(`Failed to get employee preferences: ${error.message}`);
  }

  return data;
}

/**
 * Update employee payslip preferences
 */
export async function updateEmployeePayslipPreferences(
  employeeId: string,
  updates: EmployeePayslipPreferencesUpdate
): Promise<EmployeePayslipPreferences> {
  const { data, error } = await supabase.rpc('update_employee_payslip_preferences', {
    p_employee_id: employeeId,
    p_enable_password_protection: updates.enable_password_protection,
    p_use_login_password: updates.use_login_password,
    p_custom_password: updates.custom_password,
    p_auto_download_payslips: updates.auto_download_payslips,
    p_email_payslips: updates.email_payslips,
    p_preferred_language: updates.preferred_language
  });

  if (error) {
    throw new Error(`Failed to update employee preferences: ${error.message}`);
  }

  return data;
}

/**
 * Generate password for employee payslip based on their preferences
 */
export async function generateEmployeePayslipPassword(
  employeeId: string,
  fallbackPassword?: string
): Promise<string | null> {
  try {
    const preferences = await getEmployeePayslipPreferences(employeeId);
    
    // If password protection is disabled, return null
    if (!preferences.enable_password_protection) {
      return null;
    }

    // If using login password, we'll use a fallback since we can't access auth.users directly
    if (preferences.use_login_password) {
      // In a real implementation, this would be the user's login password
      // For now, we'll use a fallback or the employee ID as a temporary solution
      return fallbackPassword || `temp_${employeeId.substring(0, 8)}`;
    }

    // If using custom password, we'll need to verify it's set
    // This is a simplified approach - in practice you'd have the user enter their custom password
    if (preferences.custom_password_hash) {
      return 'custom_password_required'; // Signal that custom password verification is needed
    }

    // Default fallback
    return fallbackPassword || `default_${employeeId.substring(0, 8)}`;
  } catch (error) {
    console.error('Error generating employee payslip password:', error);
    return fallbackPassword || null;
  }
}

/**
 * Verify password against employee preferences
 */
export async function verifyEmployeePayslipPassword(
  employeeId: string,
  password: string
): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('verify_employee_payslip_password', {
      p_employee_id: employeeId,
      p_password: password
    });

    if (error) {
      console.error('Error verifying employee payslip password:', error);
      return false;
    }

    return data;
  } catch (error) {
    console.error('Error verifying employee payslip password:', error);
    return false;
  }
}

/**
 * Get current user's employee ID
 */
export async function getCurrentUserEmployeeId(): Promise<string | null> {
  try {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) return null;

    const { data: employee, error } = await supabase
      .from('employees')
      .select('id')
      .eq('user_id', user.user.id)
      .single();

    if (error || !employee) {
      return null;
    }

    return employee.id;
  } catch (error) {
    console.error('Error getting current user employee ID:', error);
    return null;
  }
}

/**
 * Get preferences for current logged-in employee
 */
export async function getCurrentEmployeePayslipPreferences(): Promise<EmployeePayslipPreferences | null> {
  const employeeId = await getCurrentUserEmployeeId();
  if (!employeeId) return null;

  try {
    return await getEmployeePayslipPreferences(employeeId);
  } catch (error) {
    console.error('Error getting current employee preferences:', error);
    return null;
  }
}

/**
 * Check if employee has password protection enabled
 */
export async function isEmployeePasswordProtectionEnabled(employeeId: string): Promise<boolean> {
  try {
    const preferences = await getEmployeePayslipPreferences(employeeId);
    return preferences.enable_password_protection;
  } catch (error) {
    console.error('Error checking password protection status:', error);
    return true; // Default to protected for security
  }
}

/**
 * Get password for payslip generation based on employee preferences
 */
export async function getPayslipPassword(employeeId: string, userProvidedPassword?: string): Promise<string | null> {
  try {
    const preferences = await getEmployeePayslipPreferences(employeeId);
    
    // If password protection is disabled, return null (no password)
    if (!preferences.enable_password_protection) {
      return null;
    }

    // If using login password and user provided it, use that
    if (preferences.use_login_password && userProvidedPassword) {
      return userProvidedPassword;
    }

    // If using custom password and user provided it, verify and use
    if (!preferences.use_login_password && userProvidedPassword) {
      const isValid = await verifyEmployeePayslipPassword(employeeId, userProvidedPassword);
      if (isValid) {
        return userProvidedPassword;
      }
    }

    // Default password generation for automatic systems
    return await generateEmployeePayslipPassword(employeeId);
  } catch (error) {
    console.error('Error getting payslip password:', error);
    return null;
  }
}